"use client"

import { useState, useEffect, useRef } from "react"
import { MapPin, Navigation, MapPinIcon, Home, Building } from "lucide-react"
import "./Location.css"
import { useLocations } from "../../utils/LocationContext"
import { useAuth } from "../../utils/AuthContext"
import { geolocationService } from "../../services/geolocationService"
import { SafariGeolocationService } from "../../services/safariGeolocationService"
import { toast } from 'react-toastify'
import { getLocationByName } from "../../services/locationsApi"

const Location = ({ onLocationChange, initialLocation = "", showDetailedInfo = false }) => {
  const {
    locations,
    loadingLocations,
    detectLocationFromGPS,
    // Usar el estado global de ubicación
    selectedGlobalLocation,
    updateGlobalLocation,
    noLocationSelected
  } = useLocations()

  const { userData, justLoggedIn, setJustLoggedIn } = useAuth()
  const [selectedLocation, setSelectedLocation] = useState("")
  const [locationDetails, setLocationDetails] = useState(null)
  const initialRenderRef = useRef(true)
  const userLocationAppliedRef = useRef(false)
  const explicitSelectionRef = useRef(false)

  const [geoLocationStatus, setGeoLocationStatus] = useState('idle')
  const [showLocationPrompt, setShowLocationPrompt] = useState(false)
  const [isSafari, setIsSafari] = useState(false)

  // Detectar Safari al montar el componente
  useEffect(() => {
    setIsSafari(SafariGeolocationService.isSafari())
  }, [])

  // Sincronizar el estado local con el estado global al montar y cuando cambie
  useEffect(() => {
    if (noLocationSelected) {
      // El usuario eligió explícitamente no seleccionar ubicación
      setSelectedLocation("")
      explicitSelectionRef.current = true
      return
    }
    if (selectedGlobalLocation && selectedGlobalLocation.name) {
      setSelectedLocation(selectedGlobalLocation.name);
    } else {
      setSelectedLocation("");
    }
  }, [selectedGlobalLocation, noLocationSelected])

  const requestExactLocation = async () => {
    setGeoLocationStatus('requesting-exact')

    // Detectar si estamos en un navegador embebido como Instagram
    const inAppBrowser = SafariGeolocationService.isInAppBrowser();
    if (inAppBrowser) {
      toast.error(`No se puede acceder a tu ubicación en el navegador de ${inAppBrowser.name}. Por favor, abre esta página en tu navegador normal (Chrome, Safari, etc.) para utilizar el GPS.`, {
        position: "top-center",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setGeoLocationStatus('error');
      return;
    }

    // Verificar contexto seguro para Safari
    if (isSafari && !SafariGeolocationService.isSecureContext()) {
      toast.error('Necesitas una conexión segura (HTTPS) para acceder a tu ubicación.', {
        position: "top-center",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
      setGeoLocationStatus('error')
      return
    }

    try {
      const coords = await geolocationService.getExactLocation()
      await handleGeolocationSuccess(coords, 'exact')
    } catch (error) {
      console.log('Ubicación exacta denegada:', error.message)
      setShowLocationPrompt(true)
    }
  }

  const requestApproximateLocation = async () => {
    setGeoLocationStatus('requesting-approximate')
    setShowLocationPrompt(false)

    try {
      const coords = await geolocationService.getApproximateLocation()
      await handleGeolocationSuccess(coords, 'approximate')
    } catch (error) {
      toast.error('No se pudo obtener tu ubicación. Puedes seleccionar manualmente.', {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
      setGeoLocationStatus('error')
      setShowLocationPrompt(false)
    }
  }

  const handleGeolocationSuccess = async (coords, type) => {
    // Guardar la ubicación seleccionada previamente
    const previousLocation = selectedLocation;
    try {
      // Usar el servicio centralizado que maneja toda la lógica
      const detectionResult = await detectLocationFromGPS(coords.latitude, coords.longitude);
      if (detectionResult && detectionResult.success) {
        const { systemLocation, displayName, isSystemLocation } = detectionResult;
        if (isSystemLocation) {
          // Si es una ubicación del sistema, seleccionarla y buscar en el backend
          setSelectedLocation(systemLocation.name);
          try {
            const updatedLocation = await getLocationByName(systemLocation.name);
            setLocationDetails(updatedLocation);
          } catch (error) {
            setLocationDetails(null);
            console.error("Error obteniendo ubicación actualizada por GPS:", error);
          }

          // Actualizar el estado global de ubicación, INDICANDO explícitamente que es de GPS
          updateGlobalLocation(systemLocation, true);

          // Marcar explícitamente como selección explícita para prevenir sobreescrituras
          explicitSelectionRef.current = true;

          // Notificar cambio al componente padre (si existe)
          if (onLocationChange) {
            onLocationChange(systemLocation, coords, true);
          }

          toast.success(`📍 Ubicación detectada: ${systemLocation.name}`, {
            position: "top-center",
            autoClose: 3000
          });
        } else {
          // Si NO es una ubicación del sistema, lanzar toast.warn y restaurar selección previa
          toast.warn(
            `La ubicación detectada (${displayName}) no es válida en el sistema. Selecciona una ubicación cercana manualmente.`,
            {
              position: "top-center",
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
          // Restaurar selección previa o placeholder
          if (previousLocation) {
            setSelectedLocation(previousLocation);
          } else {
            setSelectedLocation("");
          }
          setLocationDetails(null);
          // No actualizar el estado global
        }
        setGeoLocationStatus('success');
        setShowLocationPrompt(false);
      } else {
        console.error('Error en detectionResult:', detectionResult);
        toast.error('Error procesando la ubicación');
        setGeoLocationStatus('error');
      }
    } catch (error) {
      console.error('Error procesando ubicación:', error);
      toast.error('Error procesando la ubicación');
      setGeoLocationStatus('error');
    }
  }

  const formatLocationName = (geoData) => {
    const { city, state, country, fullAddress } = geoData

    if (fullAddress) {
      if (city && state && city !== state) {
        return `${city}, ${state}`
      }

      if (city) {
        return city
      }

      if (fullAddress.quarter && fullAddress.quarter !== city) {
        return state ? `${fullAddress.quarter}, ${state}` : fullAddress.quarter
      }

      if (fullAddress.suburb && fullAddress.suburb !== city) {
        return state ? `${fullAddress.suburb}, ${state}` : fullAddress.suburb
      }

      if (state) {
        return state
      }

      if (country) {
        return country
      }
    }

    return geoData.displayName || 'Ubicación detectada'
  }

  const renderLocationDetails = () => {
    if (!showDetailedInfo || !locationDetails || locationDetails.isCoordinatesOnly) {
      return null
    }

    const { fullAddress } = locationDetails

    return (
        <div className="location-details-card">
          <div className="location-details-header">
            <MapPinIcon size={16} />
            <span>Detalles de ubicación detectada</span>
          </div>
          <div className="location-details-content">
            {fullAddress?.road && (
                <div className="location-detail-item">
                  <Home size={14} />
                  <span>
                                {fullAddress.house_number ? `${fullAddress.house_number} ` : ''}
                    {fullAddress.road}
                            </span>
                </div>
            )}
            {fullAddress?.quarter && (
                <div className="location-detail-item">
                  <Building size={14} />
                  <span>{fullAddress.quarter}</span>
                </div>
            )}
            {locationDetails.city && (
                <div className="location-detail-item">
                  <MapPin size={14} />
                  <span>{locationDetails.city}, {locationDetails.state}</span>
                </div>
            )}
            {fullAddress?.postcode && (
                <div className="location-detail-item">
                  <span className="postcode">CP: {fullAddress.postcode}</span>
                </div>
            )}
          </div>
        </div>
    )
  }

  const useManualSelection = () => {
    setShowLocationPrompt(false)
    setGeoLocationStatus('idle')
  }

  // Efecto para manejar el inicio de sesión del usuario
  useEffect(() => {
    if (noLocationSelected) return // Respetar no selección
    if (justLoggedIn && userData?.location && locations.length > 0) {
      const userLocation = locations.find(loc => loc.name === userData.location)
      if (userLocation) {
        // Forzar la ubicación del perfil del usuario
        setSelectedLocation(userLocation.name)
        if (onLocationChange) {
          onLocationChange(userLocation)
        }
        // Marcar que la ubicación del perfil fue aplicada
        userLocationAppliedRef.current = true
        // Resetear la selección explícita para que esta nueva ubicación no bloquee futuras selecciones
        explicitSelectionRef.current = false
        // Consumir el estado de "justLoggedIn"
        setJustLoggedIn(false)
      }
    }
  }, [justLoggedIn, userData, locations, onLocationChange, setJustLoggedIn, noLocationSelected])

  useEffect(() => {
    if (!locations || locations.length === 0) return

    // Si el usuario ha hecho una selección explícita, no hacer nada.
    if (explicitSelectionRef.current) {
      initialRenderRef.current = false
      return
    }

    // Si el usuario eligió explícitamente NO tener ubicación, respetar y no aplicar nada
    if (noLocationSelected) {
      setSelectedLocation("")
      if (onLocationChange) onLocationChange(null)
      explicitSelectionRef.current = true
      initialRenderRef.current = false
      return
    }

    // 1. PRIORIDAD: Ubicación guardada en localStorage (selección explícita previa)
    const storedLocation = localStorage.getItem("selectedLocation")
    if (storedLocation) {
      const parsedLocation = JSON.parse(storedLocation)
      const exists = locations.find((loc) => loc.name === parsedLocation.name)

      if (exists) {
        setSelectedLocation(parsedLocation.name)
        if (onLocationChange) {
          onLocationChange(parsedLocation)
        }
        explicitSelectionRef.current = true // Marcar como explícita para evitar sobreescritura
        initialRenderRef.current = false
        return
      }
    }

    // 2. Ubicación del perfil del usuario (si no hay nada en localStorage)
    if (userData && userData.location && !userLocationAppliedRef.current) {
      const userLocation = locations.find(loc => loc.name === userData.location)
      if (userLocation) {
        setSelectedLocation(userLocation.name)
        if (onLocationChange) {
          onLocationChange(userLocation)
        }
        userLocationAppliedRef.current = true
        initialRenderRef.current = false
        return
      }
    }

    // 3. Ubicación inicial pasada como prop
    if (initialLocation && initialRenderRef.current) {
      const found = locations.find((loc) => loc.name === initialLocation)
      if (found && found.name !== selectedLocation) {
        setSelectedLocation(found.name)
        if (onLocationChange) {
          onLocationChange(found)
        }
      }
      initialRenderRef.current = false
      return
    }

    initialRenderRef.current = false
  }, [locations, initialLocation, userData, noLocationSelected])

  useEffect(() => {
    // Resetear userLocationAppliedRef si el usuario cambia, para que se pueda aplicar la nueva ubicación del perfil.
    // No resetear si hay una selección explícita.
    if (!explicitSelectionRef.current) {
      userLocationAppliedRef.current = false
    }
  }, [userData])

  const handleLocationChange = async (e) => {
    const name = e.target.value;
    setSelectedLocation(name);
    explicitSelectionRef.current = true;
    userLocationAppliedRef.current = true; // Marcar que la ubicación del usuario ya no es la prioritaria
    setGeoLocationStatus('idle');

    // Si el valor seleccionado está vacío, limpiar la ubicación
    if (!name) {
      // Limpiar el estado global si no hay ubicación seleccionada
      updateGlobalLocation(null, false);
      setLocationDetails(null);
      if (onLocationChange) {
        onLocationChange(null);
      }
      return;
    }

    const fullLocation = locations.find((loc) => loc.name === name);
    if (fullLocation) {
      // Actualizar el estado global de ubicación (indicando que NO es de GPS)
      updateGlobalLocation(fullLocation, false);

      if (onLocationChange) {
        onLocationChange(fullLocation);
      }
      // Obtener datos actualizados desde el backend
      try {
        const updatedLocation = await getLocationByName(name);
        setLocationDetails(updatedLocation);
      } catch (error) {
        setLocationDetails(null);
        console.error("Error obteniendo ubicación actualizada:", error);
      }
    } else {
      // Limpiar el estado global si no hay ubicación seleccionada
      updateGlobalLocation(null);

      setLocationDetails(null);
      if (onLocationChange) {
        onLocationChange(null);
      }
    }
  }

  return (
      <div className="location-bar">
        <div className="location-container">
          <div className="location-wrapper">
            <MapPin className="location-icon" size={24} />

            <select
                className="location-select2"
                value={selectedLocation}
                onChange={handleLocationChange}
                disabled={loadingLocations}
            >
              <option value="">
                {geoLocationStatus.includes('requesting')
                    ? 'Obteniendo tu ubicación...'
                    : 'Seleccioná una ubicación'
                }
              </option>
              {Array.isArray(locations) &&
                  locations
                      .sort((a, b) => (a.order || 999) - (b.order || 999))
                      .map((loc) => (
                          <option key={loc.name} value={loc.name}>
                            {loc.name}
                          </option>
                      ))}
            </select>

            <button
                className="geolocation-btn"
                onClick={requestExactLocation}
                disabled={geoLocationStatus === 'requesting-exact' || geoLocationStatus === 'requesting-approximate'}
                title="Usar mi ubicación actual"
            >
              <Navigation
                  size={18}
                  className={geoLocationStatus.includes('requesting') ? 'location-loading' : ''}
              />
              {!geoLocationStatus.includes('requesting') && (
                  <span className={`geo-btn-text ${isSafari ? 'safari-mode' : ''}`}>GPS</span>
              )}
            </button>
          </div>

          {/* Prompt para ubicación aproximada */}
          {showLocationPrompt && (
              <div className="location-prompt">
                <p><strong>¿Podemos acceder a tu zona general?</strong></p>
                <p>Solo usaremos tu ciudad, no tu ubicación exacta</p>
                <div className="button-group">
                  <button onClick={requestApproximateLocation}>
                    Sí, usar zona
                  </button>
                  <button onClick={useManualSelection}>
                    Elegir manualmente
                  </button>
                </div>
              </div>
          )}

          {/* Mostrar detalles de ubicación */}
          {renderLocationDetails()}
        </div>
      </div>
  )
}

export default Location